/**
 * `rule-preview` — the assistant's "here is the rule I would build" artifact
 * (TV1-2340 · P4).
 *
 * The `compose_rule` capability tool turns a seller's prose into a rule GRAPH.
 * This block draws that graph inside the chat, using the very same read-only
 * `RuleNodesView` the dry-run and execution-trace modals use — the graph object
 * is a superset-compatible match for the editor's own wire format, so it is
 * handed over verbatim (`RuleNodesView` YAML-stringifies it and the editor
 * re-parses). No adapter, and no second renderer to keep in sync.
 *
 * ─── 🚨 THE DEMO STOPS HERE ─────────────────────────────────────────────────
 * Nothing on this path is persisted, activated, dry-run or executed. There is
 * no backend call, no rule id, no draft. Every string in this card is written
 * to say exactly that: a seller who reads "rule created" and walks away is the
 * one failure this artifact is capable of causing, so the copy must never imply
 * it. Do not add a Save/Activate action here without building the write path.
 *
 * ─── Why a guard AND a boundary ─────────────────────────────────────────────
 * `parseDsl` THROWS on a dangling edge (`Edge target not found: <to>`), and
 * that is the composer's most frequent failure mode — 32 occurrences in the
 * recorded 20-case run. So:
 *   • `findDanglingEdgeTargets` classifies that case up-front, in the stream
 *     adapter, and this card renders a named failure instead of mounting;
 *   • the boundary below catches every OTHER parse throw (an unknown node type,
 *     a malformed `values`), degrading to the same failure card rather than
 *     blanking the chat panel.
 * One without the other is not enough: the guard cannot know every throw the
 * editor has, and a boundary alone would turn the COMMON failure into a
 * generic apology that names nothing.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconAlertTriangle, IconWand } from '@tabler/icons-react';
import type { MessageBlockRendererProps } from '@domains/aiAssistant/state/messageBlocks';
import type { RulePreviewPayload } from '@domains/aiAssistant/state/rulePreview';
import { componentsTestIds } from '@components/testids';
import classes from './RulePreviewBlock.module.css';

export type RulePreviewBlockPayload = RulePreviewPayload;

/**
 * The node editor is LAZY, and not only for bundle weight.
 *
 * `blocks/index.ts` is imported for its side effect by the chat panel, so a
 * static import here drags `RuleNodesView` — and with it `RuleEditor` plus the
 * generated node registry — into the module graph of everything that reaches
 * the chat. That registry resolves through a `vite.config` alias and an ambient
 * `declare module` (`src/vite-env.d.ts`), NOT through `tsconfig` `paths`, and
 * `vitest.config` derives its aliases from `tsconfig` — so a static import
 * breaks COLLECTION of every suite whose graph touches the chat panel, with a
 * `Cannot find module '@bett3r-dev/teselly-rule-editor/registry'`. (Verified:
 * it took down `messageBlockRegistry.test.ts` and `suspensionBlockRetired.test.ts`.)
 *
 * Deferring the import to render time keeps the editor out of every graph that
 * never draws a rule. The underlying config gap is real but out of scope here:
 * the fix is teaching `vitest.config` the same subpath alias `vite.config`
 * already hardcodes.
 */
const RuleNodesView = React.lazy( async () => ({
  default: ( await import( '@components/application/Rules/RuleNodesView/RuleNodesView' )).RuleNodesView
}));

/** Bounded so the graph never pushes the conversation out of the panel. */
const GRAPH_HEIGHT = 300;

/**
 * Minimal boundary around the editor mount. Class component because React has
 * no hook form of `componentDidCatch`. It renders the caller's `fallback` and
 * deliberately does NOT re-throw: the global error handler exists for actions
 * the user took, not for decoration on a message.
 */
class GraphErrorBoundary extends React.Component<
  { fallback: React.ReactNode; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch( error: unknown ) {
    console.error( '[rule-preview] the composed graph could not be rendered:', error );
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const isPayload = ( value: unknown ): value is RulePreviewPayload =>
  typeof value === 'object' && value !== null && typeof ( value as { status?: unknown }).status === 'string';

export const RulePreviewBlock = ({ block }: MessageBlockRendererProps ) => {
  const { t } = useTranslation( 'interactions' );
  const payload = block.payload;

  // A payload this build does not understand degrades to the message's own
  // text, exactly as an unregistered block type does.
  if ( !isPayload( payload )) return null;

  const failure = ( title: string, detail: string ) => (
    <div className={classes.block} data-testid={componentsTestIds.aiRulePreview}>
      <div className={classes.head}>
        <span className={classes.errMark}><IconAlertTriangle size={12} stroke={2.4} /></span>
        <span className={classes.title}>{title}</span>
      </div>
      <p className={classes.body} data-testid={componentsTestIds.aiRulePreviewFailure}>{detail}</p>
    </div>
  );

  if ( payload.status === 'gap' ) {
    return failure(
      t( 'ai_rule_preview_gap_title', 'No pude armar esta regla' ),
      // `gapDescription` is authored by the tool in Spanish, for the seller —
      // it is the explanation, not a code, so it is shown verbatim.
      payload.gapDescription
    );
  }

  if ( payload.status === 'invalid' ) {
    return failure(
      t( 'ai_rule_preview_invalid_title', 'Armé una regla que no puedo dibujar' ),
      t(
        'ai_rule_preview_invalid_detail',
        'La regla quedó con conexiones que no apuntan a ningún paso ({{targets}}). Contame de nuevo qué querés que pase y lo intento otra vez.',
        { targets: payload.targets.join( ', ' ) }
      )
    );
  }

  const { graph, attempts, repaired } = payload;

  return (
    <div className={classes.block} data-testid={componentsTestIds.aiRulePreview}>
      <div className={classes.head}>
        <span className={classes.mark}><IconWand size={12} stroke={2.2} /></span>
        <span className={classes.title}>{t( 'ai_rule_preview_title', 'Así se vería la regla' )}</span>
      </div>

      <GraphErrorBoundary
        fallback={
          <p className={classes.body} data-testid={componentsTestIds.aiRulePreviewFailure}>
            {t(
              'ai_rule_preview_unrenderable',
              'Armé la regla pero no la puedo dibujar acá. Probá pidiéndomela de otra forma.'
            )}
          </p>
        }
      >
        <div className={classes.graph} data-testid={componentsTestIds.aiRulePreviewGraph}>
          <React.Suspense fallback={<div className={classes.loading} />}>
            <RuleNodesView dsl={graph} height={GRAPH_HEIGHT} />
          </React.Suspense>
        </div>
      </GraphErrorBoundary>

      {/*
        Shown, never hidden. On prose input, self-correction is the NORMAL path
        (both successes in the P2 probe failed their first pass), so suppressing
        this would hide the assistant's usual behaviour and make the one visible
        failure look exceptional.
      */}
      {( repaired || attempts > 1 ) && (
        <p className={classes.note} data-testid={componentsTestIds.aiRulePreviewRepaired}>
          {t(
            'ai_rule_preview_repaired',
            'El primer intento me salió mal y lo corregí solo ({{attempts}} intentos).',
            { attempts }
          )}
        </p>
      )}

      {/* The honesty line. Not optional — see the module docblock. */}
      <p className={classes.note} data-testid={componentsTestIds.aiRulePreviewNotSaved}>
        {t(
          'ai_rule_preview_not_saved',
          'Es sólo una vista previa: todavía no creé ni activé nada.'
        )}
      </p>
    </div>
  );
};

export default RulePreviewBlock;
